import { useGetProductsQuery } from "api/public";
import { Product, productsSchema } from "api/schema/productsSchema";
import { ApiError } from "api/shared";
import { AxiosError, AxiosPromise, AxiosRequestConfig } from "axios";
import { RefetchOptions } from "axios-hooks";
import React, { createContext, useContext } from "react";
import { getDefaultProductStatus, setEnabledStatus } from "utils";

type EnabledProductsKeys =
  | "isLendingEnabled"
  | "isBankFeedsEnabled"
  | "isSyncForCommerceEnabled"
  | "isSyncForExpensesV1Enabled"
  | "isSyncForExpensesV2Enabled"
  | "isSpendInsightsEnabled";

type ProductsEnabledStatus = Record<EnabledProductsKeys, boolean>;

interface ProductsContextType {
  products: Product[];
  error?: AxiosError<ApiError> | null;
  refetch: (
    config?: AxiosRequestConfig | undefined,
    options?: RefetchOptions | undefined
  ) => AxiosPromise<Product[]>;
  productsEnabledStatus: ProductsEnabledStatus;
}

export const ProductsContext = createContext<ProductsContextType>({
  productsEnabledStatus: getDefaultProductStatus(),
  products: [],
  refetch: () => {
    throw new Error("ProductsContext needs to be initialised before use");
  },
});

export const ProductsContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [{ data: fetchedProducts, error }, refetch] = useGetProductsQuery();
  const parsedProducts =
    (fetchedProducts && productsSchema.safeParse(fetchedProducts))?.data ?? [];

  const enabledProducts = setEnabledStatus(parsedProducts);
  return (
    <ProductsContext.Provider
      value={{
        products: parsedProducts,
        error,
        refetch,
        productsEnabledStatus: enabledProducts,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = () => useContext(ProductsContext);

export const useProduct = (productId: string | undefined) => {
  const context = useContext(ProductsContext);
  return {
    error: context.error,
    refetch: context.refetch,
    product: productId
      ? context.products.find((product) => product.productId === productId)
      : undefined,
  };
};
